import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { startPageCapture } from "./capturePage";
import { FeedbackModal } from "./FeedbackModal";

interface FeedbackDialog {
  open: () => void;
}

/** One opening of the dialog: the page it was opened on (sent as page_url) and its screenshot. */
interface FeedbackSession {
  pageUrl: string;
  capture: Promise<File | null>;
}

const FeedbackContext = createContext<FeedbackDialog | null>(null);

/**
 * The Send feedback dialog for the signed-in shell, opened with useFeedbackDialog(). It lives here,
 * not in the sidebar, so it survives the mobile drawer closing (the drawer unmounts its content).
 */
export function FeedbackProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [session, setSession] = useState<FeedbackSession | null>(null);
  const open = useCallback(() => {
    // Both are taken now, as it opens (if it's already open, the open session is kept).
    const next = { pageUrl: globalThis.location.href, capture: startPageCapture() };
    setSession((current) => current ?? next);
  }, []);
  const close = useCallback(() => setSession(null), []);
  const value = useMemo(() => ({ open }), [open]);
  return (
    <FeedbackContext.Provider value={value}>
      {children}
      {session && <FeedbackModal pageUrl={session.pageUrl} capture={session.capture} onClose={close} />}
    </FeedbackContext.Provider>
  );
}

const NONE: FeedbackDialog = { open: () => {} };

/** Open the Send feedback dialog. */
export function useFeedbackDialog(): FeedbackDialog {
  return useContext(FeedbackContext) ?? NONE;
}
